import {Trans} from '@lingui/macro'
import type {FC} from 'react'
import styled from 'styled-components'

import {Link, List, ListItem, ListIcon, tokens} from '@pleo-io/telescope'
import {Check, Close} from '@pleo-io/telescope-icons'

import {PlanType} from '~/api/types/plan'

import {
    getPlanNameCamelCase,
    getPlanTypeBenefits,
    availablePlansPreferredOrder,
    isProOrPremium,
    availableTeamPlansPreferredOrder,
} from './get-plan-details'

const Flex = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: center;
`

const ListHeader = styled.div`
    padding-bottom: ${tokens.spacing10};
`

const ComparePlansWrapper = styled.div`
    padding-top: ${tokens.spacing10};
`

const ListWrapper = styled.div`
    max-width: 300px;
`

interface FeaturesListProps {
    isCurrent: boolean
    plan: PlanType
    planSelected: PlanType
}

export const FeaturesList: FC<FeaturesListProps> = ({isCurrent, plan, planSelected}) => {
    const planTypeBenefits = getPlanTypeBenefits()
    const planNameCamelCaseList = getPlanNameCamelCase()

    const isUpgrade = isProOrPremium.includes(plan)
        ? availablePlansPreferredOrder.indexOf(planSelected) >
          availablePlansPreferredOrder.indexOf(plan)
        : availableTeamPlansPreferredOrder.indexOf(planSelected) >
          availableTeamPlansPreferredOrder.indexOf(plan)

    const isDowngrade = isProOrPremium.includes(plan)
        ? availablePlansPreferredOrder.indexOf(planSelected) <
          availablePlansPreferredOrder.indexOf(plan)
        : availableTeamPlansPreferredOrder.indexOf(planSelected) <
          availableTeamPlansPreferredOrder.indexOf(plan)

    const isCurrentOrUpgrade = isCurrent || isUpgrade
    const isUpgradeToPremium =
        isUpgrade && [PlanType.PREMIUM, PlanType.YEARLY].includes(planSelected)
    const isUpgradeToBusiness = isUpgrade && planSelected === PlanType.BUSINESS
    const isUpgradeToPro = isUpgrade && planSelected === PlanType.PRO
    const isUpgradeToTeam = isUpgrade && planSelected === PlanType.TEAM

    const premiumUpgradeHeader = <Trans>All the benefits of Pro, plus</Trans>
    const proUpgradeHeader = <Trans>Benefits of Pro</Trans>

    const businessUpgradeHeader = <Trans>All the benefits of Team, plus</Trans>
    const teamUpgradeHeader = <Trans>Benefits of Team</Trans>

    const currentHeader = <Trans>{planNameCamelCaseList[plan]} benefits you have access to</Trans>
    const loseHeader = <Trans>Benefits you lose</Trans>

    const getFeatures = () => {
        if (isCurrent || isUpgrade) {
            return planTypeBenefits[planSelected]
        } else if (isDowngrade && planSelected === PlanType.FREE) {
            // show feature list that user will lose, in this case pro benefits
            return planTypeBenefits[PlanType.PRO]
        } else if (isDowngrade && [PlanType.PRO, PlanType.TEAM].includes(planSelected)) {
            // show feature list that user will lose, in this case premium benefits
            return planTypeBenefits[PlanType.PREMIUM]
        }

        return planTypeBenefits[planSelected]
    }

    return (
        <Flex>
            <ListWrapper>
                <ListHeader>
                    {isUpgradeToPremium && premiumUpgradeHeader}
                    {isUpgradeToBusiness && businessUpgradeHeader}
                    {isUpgradeToPro && proUpgradeHeader}
                    {isUpgradeToTeam && teamUpgradeHeader}
                    {isCurrent && currentHeader}
                    {!isCurrentOrUpgrade && loseHeader}
                </ListHeader>
                <List>
                    {getFeatures().map((item) => (
                        <ListItem key={item}>
                            {isCurrentOrUpgrade ? (
                                <ListIcon as={Check} color={tokens.pink700} size={16} />
                            ) : (
                                <ListIcon as={Close} color={tokens.shade600} size={16} />
                            )}
                            {item}
                        </ListItem>
                    ))}
                </List>
                <ComparePlansWrapper>
                    <Link
                        href="https://www.pleo.io/pricing/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <Trans>Compare plans</Trans>
                    </Link>
                </ComparePlansWrapper>
            </ListWrapper>
        </Flex>
    )
}
